import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Calendar, User, Clock, ArrowRight, ArrowLeft } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { translations } from '@/lib/translations';
import { Link } from 'react-router-dom';

export function BlogPage() {
  const { language } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();

  // Blog articles data
  const blogArticles = [
    {
      id: "conferenza-panama-la-habana",
      title: {
        it: "Pratiche migratorie, visti e permessi di residenza e lavoro a Panama",
        es: "Prácticas migratorias, visados y permisos de residencia y trabajo en Panamá",
        en: "Migration practices, visas, and residence & work permits in Panama"
      },
      excerpt: {
        it: "Conferenza esclusiva all’Avana con Arcia–Pujol (Panamá). Venerdì 14 novembre, ore 16:00, La Maison — prenotazione (CUP 2.000).",
        es: "Conferencia exclusiva en La Habana con Arcia–Pujol (Panamá). Viernes 14 de noviembre, 4:00 p.m., La Maison — reserva (CUP 2,000).",
        en: "Exclusive conference in Havana with Arcia–Pujol (Panama). Friday, November 14, 4:00 p.m., La Maison — reservation (CUP 2,000)."
      },
      date: "2025-11-14",
      image: "/rs-logo.svg",
      category: {
        it: "Eventi",
        es: "Eventos",
        en: "Events"
      },
      author: {
        it: "RS International Services e Arcia–Pujol (Panamá)",
        es: "RS International Services y Arcia–Pujol (Panamá)",
        en: "RS International Services and Arcia–Pujol (Panama)"
      },
      readTime: 4
    },
    {
      id: "dazi-usa-prodotti-italiani",
      title: {
        it: "Dazi USA sui Prodotti Italiani: Analisi Completa e Viabilità della Triangolazione via Panama",
        es: "Aranceles de EE.UU. sobre Productos Italianos: Análisis Completo y Viabilidad de la Triangulación vía Panamá",
        en: "US Tariffs on Italian Products: Complete Analysis and Viability of Triangulation via Panama"
      },
      excerpt: {
        it: "Analisi approfondita dei dazi americani sui prodotti italiani nell'ottobre 2025, focalizzandosi sul dazio del 107% per la pasta, regolamentazioni anti-evasione, e perché la triangolazione attraverso Panama è illegale ed economicamente insostenibile.",
        es: "Análisis en profundidad de los aranceles estadounidenses sobre productos italianos en octubre de 2025, enfocándose en el arancel del 107% para la pasta, regulaciones anti-evasión, y por qué la triangulación a través de Panamá es ilegal e insostenible económicamente.",
        en: "In-depth analysis of US tariffs on Italian products in October 2025, focusing on the 107% tariff for pasta, anti-evasion regulations, and why triangulation through Panama is illegal and economically unsustainable."
      },
      date: "2025-10-05",
      image: "/articolo_dazi.png",
      category: {
        it: "Commercio Internazionale",
        es: "Comercio Internacional",
        en: "International Trade"
      },
      author: {
        it: "RS International Services",
        es: "RS International Services",
        en: "RS International Services"
      },
      readTime: 18
    },
    {
      id: "guida-residenza-panama",
      title: {
        it: "Guida Completa alla Residenza a Panama per Cittadini Italiani",
        es: "Guía Completa de Residencia en Panamá para Ciudadanos Italianos",
        en: "Complete Guide to Panama Residence for Italian Citizens"
      },
      excerpt: {
        it: "Scopri tutto quello che devi sapere per ottenere la residenza a Panama: requisiti, documenti necessari, tempi e costi. Una guida completa per cittadini italiani che vogliono trasferirsi a Panama.",
        es: "Descubre todo lo que necesitas saber para obtener la residencia en Panamá: requisitos, documentos necesarios, tiempos y costos. Una guía completa para ciudadanos italianos que quieren mudarse a Panamá.",
        en: "Discover everything you need to know to obtain residence in Panama: requirements, necessary documents, timeframes and costs. A complete guide for Italian citizens who want to move to Panama."
      },
      date: "2025-01-15",
      image: "/art_residenza_italiani.png",
      category: {
        it: "Residenza",
        es: "Residencia", 
        en: "Residence"
      },
      author: {
        it: "RS International Services",
        es: "RS International Services",
        en: "RS International Services"
      },
      readTime: 8
    },
    {
      id: "societa-offshore-panama",
      title: {
        it: "Società Offshore a Panama: Vantaggi e Procedure",
        es: "Sociedades Offshore en Panamá: Ventajas y Procedimientos",
        en: "Panama Offshore Companies: Complete Advantages and Procedures"
      },
      excerpt: {
        it: "Guida completa alla costituzione di società offshore a Panama: vantaggi fiscali, procedure legali, costi dettagliati e opportunità di business per imprenditori internazionali.",
        es: "Guía completa para establecer sociedades offshore en Panamá: ventajas fiscales, procedimientos legales, costos detallados y oportunidades de negocio para emprendedores internacionales.",
        en: "Complete guide to establishing an offshore company in Panama: tax advantages, legal procedures, detailed costs and business opportunities for international entrepreneurs."
      },
      date: "2024-01-15",
      image: "https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Panama%20City%20skyline%20with%20modern%20skyscrapers%20and%20financial%20district%2C%20professional%20business%20atmosphere%2C%20blue%20ocean%20and%20ships%20in%20background%2C%20corporate%20photography%20style%2C%20high%20quality%2C%20bright%20daylight&image_size=landscape_16_9",
      category: {
        it: "Business",
        es: "Negocios",
        en: "Business"
      },
      author: {
        it: "RS International Services",
        es: "RS International Services", 
        en: "RS International Services"
      },
      readTime: 12
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Back Navigation */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <Link 
            to="/" 
            className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'it' ? 'Torna alla Home' : language === 'es' ? 'Volver al Inicio' : 'Back to Home'}
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {t.blog.title}
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              {t.blog.subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Blog Articles */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogArticles.map((article) => (
                <div
                  key={article.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer"
                  onClick={() => navigate(language === 'it' ? `/blog/${article.id}` : `/${language}/blog/${article.id}`)}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title[language]}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {article.category[language]}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-3 text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {article.title[language]}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {article.excerpt[language]}
                    </p>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          <span>{new Date(article.date).toLocaleDateString(language === 'it' ? 'it-IT' : language === 'es' ? 'es-ES' : 'en-US')}</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          <span>{article.readTime} min</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-2 text-gray-400" />
                        <span className="text-sm text-gray-600">{article.author[language]}</span>
                      </div>
                      
                      <Link to={language === 'it' ? `/blog/${article.id}` : `/${language}/blog/${article.id}`}>
                        <Button variant="ghost" size="sm" className="group-hover:bg-blue-50 group-hover:text-blue-600">
                          {t.blog.readMore}
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Default export for lazy loading
export default BlogPage;